<template>
  <div class="fixed bottom-4 right-4 z-50">
    <AnnouncementCard
  v-if="styles.announcementsEnabled"
  :announcements="styles.announcements"
  :announcements-enabled="styles.announcementsEnabled"
  :is-widget-open="isOpen"
  :bubble-color="styles.bubbleColor"
  @navigate-to-faq="handleAnnouncementFaqNavigation"
  @navigate-to-chat="handleAnnouncementChatNavigation"
/>

    <!-- Chat Widget Container with mobile-responsive sizing -->
    <transition
      enter-active-class="transition-all duration-100 ease-out"
      enter-from-class="opacity-0 scale-0"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-0"
    >
      <div
        v-if="isOpen"
        :class="[
          'bg-white overflow-hidden flex flex-col transition-all duration-200 ease-in-out',
          // Mobile: Full screen
          isMobile
            ? 'fixed inset-0 w-full h-full rounded-none z-50'
            : 'rounded-2xl absolute bottom-18 right-0',
          // Desktop: Dynamic sizing when not mobile
          !isMobile && isExpanded
            ? 'w-[600px] rounded-2xl'
            : !isMobile
            ? 'w-[400px] h-[700px]'
            : '',
        ]"
        :style="[
          {
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            transformOrigin: 'bottom right', // Widget scales from bottom-right corner
          },
          // Desktop expanded: 80% viewport height with max/min constraints
          !isMobile && isExpanded
            ? {
                height: '90vh',
                maxHeight: '90vh',
                minHeight: '600px',
              }
            : {},
        ]"
      >
        <div class="flex-1 overflow-hidden relative flex flex-col">
          <!-- FAQ Screen with expand support and direct navigation -->
          <FAQScreen
            v-if="currentScreen === 'FAQScreen'"
            :isExpanded="isExpanded"
            :isMobile="isMobile"
            :styles="styles"
            :directFaq="directFaqData"
            @change-screen="switchScreen"
            @expand-faq="handleExpandWidget"
            @close-widget="isOpen = false"
            class="h-full flex-1 flex flex-col"
          />

          <!-- Chat Screen with FAQ navigation -->
          <ChatScreen
            v-else-if="currentScreen === 'ChatScreen'"
            @change-screen="switchScreen"
            @close-widget="isOpen = false"
            @expand-chat="handleExpandChat"
            @navigate-to-faq="handleFaqNavigation"
            class="h-full flex-1 flex flex-col"
            :styles="styles"
            :preselected-message="preselectedMessage"
            :is-mobile="isMobile"
            :isExpanded="isExpanded"
            :companyId="companyId"
          />

          <!-- Other screens -->
          <component
            v-else
            :is="currentScreen"
            @change-screen="switchScreen"
            @close-widget="isOpen = false"
            @expand-chat="handleExpandChat"
            @navigate-to-faq="handleFaqNavigation"
            class="h-full flex-1 flex flex-col"
            :styles="styles"
            :preselected-message="preselectedMessage"
            :is-mobile="isMobile"
            :isExpanded="isExpanded"
            :companyId="companyId"
          ></component>

          <!-- Navbar - only show when NOT expanded -->
          <NavBar
            v-if="!isExpanded"
            :currentScreen="currentScreen"
            :styles="styles"
            @change-screen="switchScreen"
          />
        </div>
      </div>
    </transition>

    <!-- Chat Bubble Button with Unread Badge -->
    <div class="relative">
      <button
        v-show="!(isMobile && isOpen)"
        @click="toggleChat"
        :style="{ backgroundColor: styles.bubbleColor || '#1a1a1a' }"
        class="cursor-pointer hover:opacity-90 text-white rounded-full p-4 transition-all duration-300 flex items-center justify-center relative overflow-hidden"
        style="
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        "
      >
        <!-- Use the ChatBubbleIcon component -->
        <ChatBubbleIcon :isOpen="isOpen" />
      </button>

      <!-- Unread Notification Badge -->
      <transition
        enter-active-class="transition-all duration-200"
        enter-from-class="scale-0 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition-all duration-200"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-0 opacity-0"
      >
        <div
          v-if="unreadCount > 0 && !isOpen"
          class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center shadow-lg animate-pulse"
          style="border: 2px solid white"
        >
          {{ unreadCount > 99 ? "99+" : unreadCount }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import HomeScreen from "../screens/HomeScreen.vue";
import ChatScreen from "../screens/NewChatScreen.vue";
import FAQScreen from "../screens/FAQScreen.vue";
import MeetingScreen from "../screens/MeetingScreen.vue";
import NavBar from "../components/NavBar.vue";
import ChatBubbleIcon from "../components/ChatBubbleIcon.vue";
import { fetchStyles } from "../lib/fetchStyles";
import { trackVisitorPresence, markVisitorInactive } from "../lib/appwrite";
import { chatStateManager } from "../lib/chatStateManager";
import { useRealtimeMessages } from "../composables/useRealtimeMessages";
import AnnouncementCard from "./AnnouncementCard.vue";
export default {
  name: "ChatWidget",
  components: {
    HomeScreen,
    ChatScreen,
    FAQScreen,
    MeetingScreen,
    NavBar,
    ChatBubbleIcon,
    AnnouncementCard,
  },
  props: {
    companyId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      currentScreen: "HomeScreen",
      styles: {},
      preselectedMessage: null,
      visitorId: null,
      trackingInterval: null,
      sessionStart: new Date().toISOString(),
      visitorData: {},
      isExpanded: false,
      isMobile: false,
      chatState: null,
      stateUnsubscribe: null,
      wasManuallylosed: false,
      unreadCount: 0,
      lastReadMessageId: null,
      directFaqData: null,
    };
  },
  async created() {
    this.checkMobile();
    await this.initStyles();
    await this.initChatState();
    await this.initVisitorTracking();
  },

  async mounted() {
    window.addEventListener("resize", this.checkMobile);
    window.addEventListener("focus", this.handlePageFocus);
    window.addEventListener("blur", this.handlePageBlur);

    this.realtimeMessages = useRealtimeMessages();

    this.loadLastReadMessage();
  },

  beforeUnmount() {
    this.stopVisitorTracking();
    window.removeEventListener("resize", this.checkMobile);
    window.removeEventListener("focus", this.handlePageFocus);
    window.removeEventListener("blur", this.handlePageBlur);

    if (this.stateUnsubscribe) {
      this.stateUnsubscribe();
    }
  },

  watch: {
    chatState: {
      handler(newState) {
        if (newState && newState.messages) {
          this.updateUnreadCount(newState.messages);
        }
      },
      deep: true,
    },
  },

  methods: {
    // Add this method to your ChatWidget.vue methods section:

    handleAnnouncementChatNavigation(chatEvent) {
      console.log("📱 Navigating to chat from announcement:", chatEvent);

      // Open the widget first if it's closed
      if (!this.isOpen) {
        this.isOpen = true;

        // Mark messages as read when opening from announcement
        this.$nextTick(async () => {
          await this.ensureMessagesUpToDate();
          this.markMessagesAsRead();
        });
      }

      // Navigate to chat screen
      this.currentScreen = "ChatScreen";
      this.preselectedMessage = null;
    },
    handleAnnouncementFaqNavigation(faqEvent) {
      // Open the widget first if it's closed
      if (!this.isOpen) {
        this.isOpen = true;

        // Mark messages as read when opening from announcement
        this.$nextTick(async () => {
          await this.ensureMessagesUpToDate();
          this.markMessagesAsRead();
        });
      }

      // Navigate to FAQ with the specific FAQ data
      this.handleFaqNavigation(faqEvent);
    },

    // 🧪 TEMPORARY TEST: Add this to your ChatWidget.vue initStyles method

    // Replace your initStyles method in ChatWidget.vue with this fixed version

    // Replace your entire initStyles method in ChatWidget.vue with this clean version:

    async initStyles() {
      try {
        if (this.companyId) {
        } else {
        }

        this.styles = await fetchStyles(this.companyId);

        // 🔧 Only add test announcements if NO real announcements exist
        // AND we're in development mode
        const isDevelopment =
          process.env.NODE_ENV === "development" ||
          window.location.hostname === "localhost";

        if (
          isDevelopment &&
          (!this.styles.announcements || this.styles.announcements.length === 0)
        ) {
          this.styles.announcementsEnabled = true;
          this.styles.announcements = [
            // 📝 TEXT TYPE - Instant (0 seconds)
            {
              id: "test-text-instant",
              type: "text",
              title: "🎉 Text Announcement",
              message: "Dies ist eine sofortige Text-Nachricht",
              enabled: true,
              delay: 0,
              order: 1,
            },

            // 🔗 LINK TYPE - 5 seconds
            {
              id: "test-link-5sec",
              type: "link",
              title: "🔗 Link Announcement",
              message: "Klicke hier für mehr Informationen",
              enabled: true,
              delay: 5,
              order: 2,
              linkUrl: "https://www.tilltech.ch",
              linkText: "Zur Website",
            },

            // 📋 FAQ TYPE - 10 seconds
            {
              id: "test-faq-10sec",
              type: "faq",
              title: "📋 FAQ Announcement",
              message:
                "Häufig gestellte Fragen und Antworten und neue fragen auch stellen",
              enabled: true,
              schedule: "always",
              delay: 10,
              order: 3,
              faqId: "685d7032000fc7640859",
              faqTitle: "Testlauf starten – bevor es live geht",
            },

            // 🖼️ IMAGE TYPE - 15 seconds
            {
              id: "test-image-15sec",
              type: "image",
              title: "📸 Image Announcement",
              message: "Ein Bild mit zusätzlichen Informationen",
              enabled: true,
              delay: 15,
              order: 4,
              linkUrl: "https://www.tilltech.ch",
              linkText: "Mehr Details",
              imageUrl:
                "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=100&h=100&fit=crop&crop=face",
              imageFileId: "test-image-file",
            },
          ];
        } else {
        }

        // Log final announcements data for debugging
      } catch (error) {
        console.error("Error loading styles:", error);
        const { getDefaultStyles } = await import("../lib/fetchStyles");
        this.styles = getDefaultStyles();

        // Only add test announcements in development mode on error
        const isDevelopment =
          process.env.NODE_ENV === "development" ||
          window.location.hostname === "localhost";

        if (isDevelopment) {
          this.styles.announcementsEnabled = true;
          this.styles.announcements = [
            {
              id: "fallback-test",
              type: "text",
              title: "Fallback Test",
              message: "Error fallback announcement",
              delay: 0,
              enabled: true,
            },
          ];
        }
      }
    },

    handleFaqNavigation(faqEvent) {
      this.directFaqData = faqEvent;
      this.currentScreen = "FAQScreen";

      if (!this.isExpanded && !this.isMobile) {
        this.isExpanded = true;
      }
    },

    async initChatState() {
      try {
        this.stateUnsubscribe = chatStateManager.subscribe((newState) => {
          this.chatState = newState;

          if (newState && newState.messages) {
            this.updateUnreadCount(newState.messages);
          }
        });

        this.chatState = await chatStateManager.initialize(this.companyId);

        if (this.chatState && this.chatState.messages) {
          this.updateUnreadCount(this.chatState.messages);
        }
      } catch (error) {
        console.error("❌ Error initializing chat state:", error);
      }
    },

    updateUnreadCount(messages) {
      if (!messages || messages.length === 0) {
        this.unreadCount = 0;
        return;
      }

      const realMessages = messages.filter(
        (msg) =>
          !msg.isTyping &&
          !msg.isTemp &&
          msg.id &&
          (typeof msg.id !== "string" || !msg.id.startsWith("typing-"))
      );

      const customerInfo = this.getCustomerInfo();
      let unreadMessages = 0;

      if (!this.lastReadMessageId) {
        unreadMessages = realMessages.filter(
          (msg) =>
            (msg.type === "agent" || msg.type === "ai") &&
            (!customerInfo || msg.senderId !== customerInfo.anonymousId)
        ).length;
      } else {
        const lastReadIndex = realMessages.findIndex(
          (msg) => msg.id === this.lastReadMessageId
        );

        if (lastReadIndex === -1) {
          unreadMessages = realMessages.filter(
            (msg) =>
              (msg.type === "agent" || msg.type === "ai") &&
              (!customerInfo || msg.senderId !== customerInfo.anonymousId)
          ).length;
        } else {
          const unreadMessagesArray = realMessages.slice(lastReadIndex + 1);
          unreadMessages = unreadMessagesArray.filter(
            (msg) =>
              (msg.type === "agent" || msg.type === "ai") &&
              (!customerInfo || msg.senderId !== customerInfo.anonymousId)
          ).length;
        }
      }

      const previousCount = this.unreadCount;
      this.unreadCount = unreadMessages;

      this.saveUnreadCount();
    },

    getCustomerInfo() {
      return this.chatState?.customer || null;
    },

    markMessagesAsRead() {
      if (
        !this.chatState ||
        !this.chatState.messages ||
        this.chatState.messages.length === 0
      ) {
        return;
      }

      const realMessages = this.chatState.messages.filter(
        (msg) =>
          !msg.isTyping &&
          !msg.isTemp &&
          msg.id &&
          (typeof msg.id !== "string" || !msg.id.startsWith("typing-"))
      );

      if (realMessages.length === 0) {
        return;
      }

      const latestRealMessage = realMessages[realMessages.length - 1];
      if (latestRealMessage && latestRealMessage.id) {
        this.lastReadMessageId = latestRealMessage.id;
        this.unreadCount = 0;

        this.saveLastReadMessage();
        this.saveUnreadCount();
      }
    },

    saveLastReadMessage() {
      if (this.companyId && this.lastReadMessageId) {
        try {
          localStorage.setItem(
            `chatly_last_read_${this.companyId}`,
            this.lastReadMessageId
          );
        } catch (error) {
          console.warn(
            "Could not save last read message to localStorage:",
            error
          );
        }
      }
    },

    loadLastReadMessage() {
      if (this.companyId) {
        try {
          const lastRead = localStorage.getItem(
            `chatly_last_read_${this.companyId}`
          );
          if (lastRead) {
            this.lastReadMessageId = lastRead;
          }
        } catch (error) {
          console.warn(
            "Could not load last read message from localStorage:",
            error
          );
        }
      }
    },

    saveUnreadCount() {
      if (this.companyId) {
        try {
          localStorage.setItem(
            `chatly_unread_count_${this.companyId}`,
            this.unreadCount.toString()
          );
        } catch (error) {
          console.warn("Could not save unread count to localStorage:", error);
        }
      }
    },

    async handlePageFocus() {
      await chatStateManager.syncWithServer();

      if (this.isOpen) {
        await this.ensureMessagesUpToDate();
        this.markMessagesAsRead();
      }
    },

    async ensureMessagesUpToDate() {
      try {
        const currentState = chatStateManager.getState();

        if (currentState.conversation && currentState.conversation.$id) {
          await chatStateManager.syncWithServer();

          const updatedState = chatStateManager.getState();
        }
      } catch (error) {
        console.error("❌ Error ensuring messages up to date:", error);
      }
    },

    handlePageBlur() {},

    switchScreen(screenData) {
      if (typeof screenData === "string") {
        if (screenData !== "FAQScreen") {
          this.directFaqData = null;
        }

        this.currentScreen = screenData;
        this.preselectedMessage = null;

        if (screenData === "ChatScreen" && this.chatState?.isActive) {
          this.markMessagesAsRead();
        }
      } else if (typeof screenData === "object") {
        if (screenData.screen !== "FAQScreen") {
          this.directFaqData = null;
        }

        this.currentScreen = screenData.screen;
        this.preselectedMessage = screenData.preselectedMessage || null;

        if (screenData.screen === "ChatScreen") {
          this.markMessagesAsRead();
        }
      }

      if (this.visitorId) {
        this.trackVisitor();
      }
    },

    handleNewChatStart() {
      chatStateManager.completeRestart();

      this.unreadCount = 0;
      this.lastReadMessageId = null;
      this.saveLastReadMessage();
      this.saveUnreadCount();

      this.directFaqData = null;
      this.currentScreen = "HomeScreen";

      this.wasManuallylosed = true;
      setTimeout(() => {
        this.wasManuallylosed = false;
      }, 5000);
    },

    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    handleExpandChat(expandedState) {
      if (!this.isMobile) {
        this.isExpanded = expandedState;
      }
    },

    handleExpandWidget() {
      if (!this.isMobile) {
        this.isExpanded = !this.isExpanded;
      }
    },

    async initVisitorTracking() {
      try {
        this.visitorId =
          "visitor_" +
          Date.now() +
          "_" +
          Math.random().toString(36).substr(2, 9);

        this.visitorData = await this.collectVisitorData();
        await this.trackVisitor();

        this.trackingInterval = setInterval(() => {
          this.trackVisitor();
        }, 30000);

        document.addEventListener(
          "visibilitychange",
          this.handleVisibilityChange
        );
        window.addEventListener("beforeunload", this.handlePageUnload);
      } catch (error) {
        console.error("Error initializing visitor tracking:", error);
      }
    },

    async collectVisitorData() {
      const data = {
        visitorId: this.visitorId,
        companyId: this.companyId || "unknown",
        websiteUrl: window.location.hostname,
        pageUrl: window.location.pathname,
        sessionStart: this.sessionStart,
        referrer: document.referrer || "direct",
        device: this.detectDevice(),
        browser: this.detectBrowser(),
        screenResolution: screen.width + "x" + screen.height,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language || navigator.userLanguage,
      };

      try {
        const geoData = await this.getLocationData();
        data.country = geoData.country;
        data.city = geoData.city;
        data.ipAddress = geoData.ip;
      } catch (error) {
        data.country = "Unknown";
        data.city = "Unknown";
      }

      return data;
    },

    detectDevice() {
      const ua = navigator.userAgent;
      if (/tablet|ipad|playbook|silk/i.test(ua)) {
        return "tablet";
      }
      if (
        /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
          ua
        )
      ) {
        return "mobile";
      }
      return "desktop";
    },

    detectBrowser() {
      const ua = navigator.userAgent;
      if (ua.includes("Chrome")) return "Chrome";
      if (ua.includes("Firefox")) return "Firefox";
      if (ua.includes("Safari")) return "Safari";
      if (ua.includes("Edge")) return "Edge";
      return "Other";
    },

    async getLocationData() {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();

        return {
          country: data.country_name || "Unknown",
          city: data.city || "Unknown",
          ip: data.ip || "Unknown",
        };
      } catch (error) {
        throw new Error("Failed to get location data");
      }
    },

    async trackVisitor() {
      try {
        if (!this.visitorId || !this.companyId) return;

        const trackingData = {
          ...this.visitorData,
          pageUrl: window.location.pathname,
          widgetOpened: this.isOpen,
          lastActivity: new Date().toISOString(),
        };

        await trackVisitorPresence(trackingData);
      } catch (error) {
        console.error("Error tracking visitor:", error);
      }
    },

    handleVisibilityChange() {
      if (document.hidden) {
      } else {
        this.trackVisitor();

        if (this.isOpen) {
          this.markMessagesAsRead();
        }
      }
    },

    handlePageUnload() {
      if (this.visitorId) {
        markVisitorInactive(this.visitorId).catch(console.error);
      }
    },

    stopVisitorTracking() {
      if (this.trackingInterval) {
        clearInterval(this.trackingInterval);
        this.trackingInterval = null;
      }

      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
      window.removeEventListener("beforeunload", this.handlePageUnload);

      if (this.visitorId) {
        markVisitorInactive(this.visitorId).catch(console.error);
      }
    },

    toggleChat() {
      if (this.isOpen) {
        this.wasManuallylosed = true;
        setTimeout(() => {
          this.wasManuallylosed = false;
        }, 2000);
      } else {
        this.$nextTick(async () => {
          await this.ensureMessagesUpToDate();
          this.markMessagesAsRead();
        });
      }

      this.isOpen = !this.isOpen;

      if (!this.isOpen && !this.isMobile) {
        this.isExpanded = false;
      }

      if (this.visitorId) {
        this.trackVisitor();
      }
    },
  },
};
</script>
